// react
import { useContext, useEffect } from 'react';

// router
import { useNavigate } from 'react-router-dom';

// azure
import { AccountInfo, AuthenticationResult } from '@azure/msal-browser';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';

// context
import { ContextUser, ContextUserEntity } from '../App.context';

// utils
import { getToken } from '../utils/auth';
import { clearCookies } from '../utils/cookies';

// props
export interface GetTokenProps {
	account: AccountInfo;
}

export interface IdTokenClaimsProps {
	roles: string[];
	[x: string]: unknown;
}

export const useAuth = () => {
	// hooks
	const isAuthenticated = useIsAuthenticated();
	const { accounts, instance } = useMsal();
	const navigate = useNavigate();

	// context
	const { setUserEntity } = useContext(ContextUserEntity);
	const { setUser } = useContext(ContextUser);

	const clearStorage = () => {
		sessionStorage.clear();
		localStorage.clear();
		clearCookies();
	};

	const login = async () => {
		console.log('Starting login redirect...');
		try {
			// Make sure we're fully logged out before trying to log in
			if (accounts.length > 0) {
				console.log('Existing accounts found, clearing before login');
				clearStorage();
			}

			// Force account selection to avoid caching issues
			return instance.loginRedirect({
				scopes: ['user.read'],
				prompt: 'select_account',
			});
		} catch (e) {
			console.error('Login redirect error', e);
		}
	};

	const logout = async () => {
		try {
			console.log('Starting logout process...');
			// session
			const accessToken = sessionStorage.getItem('accessToken');
			const idToken = sessionStorage.getItem('idToken');

			// logout from server
			if (accessToken && idToken) {
				console.log('Calling server logout endpoint');
				try {
					await fetch(`${process.env.REACT_APP_API_URL}/logout`, {
						method: 'POST',
						headers: {
							Authorization: `Bearer ${accessToken}`,
							User: String(idToken),
						},
					});
				} catch (err) {
					console.error('Logout API error', err);
				}
			} else {
				console.log('No tokens found for server logout');
			}

			// clear sessions and auth data
			console.log('Clearing auth data');
			clearStorage();

			if (isAuthenticated) {
				console.log('Redirecting to logout via MSAL');
				// Redirect to home with postLogoutRedirectUri to ensure MSAL completes logout
				instance.logoutRedirect({
					postLogoutRedirectUri: window.location.origin,
				});
			} else {
				console.log('Not authenticated, navigating to home');
				// If not authenticated, just navigate to home
				navigate('/');
			}
		} catch (error) {
			console.error('Logout error', error);
			// On error, attempt to redirect to home page
			navigate('/');
		}
	};

	useEffect(() => {
		console.log('MSAL Auth hook running, handling redirect promise');

		instance.handleRedirectPromise().then(async (response) => {
			console.log(`Redirect promise resolved ${response ? 'with token' : 'without token'}`);

			const sessionUser = sessionStorage.getItem('user');
			const user = sessionUser ? JSON.parse(sessionUser) : null;
			console.log('Session user exists', !!user);

			if (!user?.isPersona) {
				if (accounts.length) {
					console.log('Found account(s) and getting token...');

					try {
						const resToken: AuthenticationResult = await getToken({
							account: accounts[0],
							instance,
						});

						if (resToken) {
							console.log('Token acquired successfully');

							// define tokens
							const accessToken = resToken.accessToken;
							const idToken = resToken.idToken;

							// login
							if (!user) {
								console.log('No user in session, calling login endpoint');

								try {
									const fetchLogin = await fetch(`${process.env.REACT_APP_API_URL}/login`, {
										method: 'POST',
										headers: {
											Authorization: `Bearer ${accessToken}`,
											User: String(idToken),
										},
									});

									await fetchLogin.json();
								} catch (error) {
									console.error('API login error', error);
									navigate('/?error=auth');
									return;
								}
							}

							// fetch me
							console.log('Fetching user info from /me endpoint');

							try {
								const fetchMe = await fetch(`${process.env.REACT_APP_API_URL}/me`, {
									headers: {
										Authorization: `Bearer ${accessToken}`,
										User: String(idToken),
									},
								});
								const resMe = await fetchMe.json();

								if (resMe.data) {
									console.log('User data retrieved');
									const me = resMe.data;

									// Get selected entity from session or default to first entity
									const selectedEntityIdFromSession = sessionStorage.getItem('userEntityId');
									const initialEntity =
										me.entities && me.entities.length > 0
											? typeof me.entities[0] === 'object'
												? me.entities[0]
												: null
											: null;

									// If we have a stored entity ID, find that entity in the user's entities
									let selectedEntity = initialEntity;

									if (selectedEntityIdFromSession && me.entities) {
										const entityId = parseInt(selectedEntityIdFromSession, 10);
										const foundEntity = me.entities.find((entity: unknown) => {
											// For object entities, check if it has an id property that matches
											if (entity !== null && typeof entity === 'object' && 'id' in entity) {
												return (entity as { id: number }).id === entityId;
											}
											// For number entities, compare directly
											return typeof entity === 'number' && entity === entityId;
										});

										if (foundEntity && typeof foundEntity === 'object') {
											selectedEntity = foundEntity;
										}
									}

									// set user and current entity context
									setUser(resMe.data);
									setUserEntity(selectedEntity);

									// Store the selected entity ID if we have one
									if (selectedEntity && typeof selectedEntity === 'object') {
										sessionStorage.setItem('userEntityId', String(selectedEntity.id));
									}

									if (me.status === 'DEACTIVATED') {
										console.log('User is deactivated');
										navigate('/?error=deactivated');
									} else {
										const { roles } = resToken.idTokenClaims as IdTokenClaimsProps;
										console.log('User roles', roles);

										if (roles && roles.length) {
											// set storage
											sessionStorage.setItem('user', JSON.stringify(resMe.data));

											// set user
											setUser(me);

											// set tokens
											sessionStorage.setItem('accessToken', accessToken);
											sessionStorage.setItem('idToken', idToken);

											console.log('User authenticated successfully');
										} else {
											console.log('No roles found for user');
											navigate('/?error=auth');
										}
									}
								} else if (resMe.error) {
									// Handle specific error cases
									if (resMe.error.id === 'ME_UNAUTHORIZED_GET') {
										console.log('User is not authorized');
										// Clear session first
										clearStorage();

										// Navigate to auth error page with specific error parameter
										navigate('/?error=unauthorized');
									} else if (resMe.error.id === 'USER_NOT_FOUND') {
										console.log('User not found error');
										clearStorage();
										navigate('/?error=auth');
									} else {
										// Generic error handling
										clearStorage();
										navigate('/?error=auth');
									}
								} else {
									console.log('Unknown response from /me endpoint');
									navigate('/?error=auth');
								}
							} catch (error) {
								console.error('API /me error', error);
								navigate('/?error=auth');
							}
						} else {
							console.log('Failed to get token');
							navigate('/?error=auth');
						}
					} catch (error) {
						console.error('Token acquisition error', error);
						navigate('/?error=auth');
					}
				} else {
					console.log('No accounts found after redirect');
				}
			} else {
				console.log('User is in persona mode');
			}
		});
	}, [accounts, instance, navigate, setUser, setUserEntity]);

	return {
		clearStorage,
		login,
		logout,
	};
};
